import React, { useState } from 'react';
import { ChevronLeft, Calendar, Edit, Download, Users, Tag, Clock, TrendingUp, BarChart3, LineChart as LineChartIcon } from 'lucide-react';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, LineChart, Legend } from 'recharts';

const CampaignDetail = ({ campaignId, onBack }) => {
  const [activeTab, setActiveTab] = useState('impact');
  
  const campaignData = {
    id: 1,
    name: 'Summer Fuel Discount',
    description: 'Special promotion offering discounts on premium fuel grades during summer months.',
    status: 'active',
    startDate: '2023-05-01',
    endDate: '2023-08-31',
    targetSegments: ['Urban', 'Highway'],
    stations: 42,
    kpi: {
      conversionRate: {
        value: '24.5%',
        change: '+3.5%'
      },
      averageBasket: {
        value: '฿ 850',
        change: '+12.4%'
      },
      incrementalVolume: {
        value: '87K',
        description: 'Additional liters'
      },
      roi: {
        value: '215%',
        change: '+15%'
      }
    },
    beforeAfterData: [
      { month: 'May', before: 0.81, after: 0.81 },
      { month: 'Jun', before: 0.80, after: 0.82 },
      { month: 'Jul', before: 0.79, after: 0.83 },
      { month: 'Aug', before: 0.78, after: 0.84 },
    ],
    customerSegments: [
      { name: 'Loyal Customers', percentage: 38 },
      { name: 'Occasional Customers', percentage: 45 },
      { name: 'New Customers', percentage: 17 },
    ],
    demographics: {
      male: 62,
      female: 35,
      other: 3
    },
    customerSatisfaction: {
      value: '92%',
      change: '+8%'
    }
  };
  
  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <button 
          className="mr-4 p-2 rounded-full hover:bg-gray-100"
          onClick={onBack}
        >
          <ChevronLeft size={20} className="text-gray-700" />
        </button>
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-semibold text-gray-900 mr-3">{campaignData.name}</h1>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Active
            </span>
          </div>
          <p className="text-gray-600 mt-1">{campaignData.description}</p>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap items-center">
          <div className="mr-6 mb-2">
            <div className="flex items-center text-sm text-gray-600">
              <Calendar size={16} className="mr-1" />
              Campaign period:
            </div>
            <div className="text-gray-900 font-medium">
              {new Date(campaignData.startDate).toLocaleDateString()} - {new Date(campaignData.endDate).toLocaleDateString()}
            </div>
          </div>
          
          <div className="mr-6 mb-2">
            <div className="flex items-center text-sm text-gray-600">
              <Users size={16} className="mr-1" />
              Target segments:
            </div>
            <div className="text-gray-900 font-medium">
              {campaignData.targetSegments.join(', ')}
            </div>
          </div>
          
          <div className="mb-2">
            <div className="flex items-center text-sm text-gray-600">
              <Tag size={16} className="mr-1" />
              Target stations:
            </div>
            <div className="text-gray-900 font-medium">
              {campaignData.stations} ({campaignData.targetSegments.join(', ')})
            </div>
          </div>
          
          <div className="ml-auto flex space-x-2">
            <button className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
              <Download size={16} className="mr-1" />
              Export
            </button>
            <button className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
              <Edit size={16} className="mr-1" />
              Edit
            </button>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600 mb-1">Conversion Rate</p>
          <div className="flex items-baseline">
            <h3 className="text-2xl font-semibold text-gray-900">{campaignData.kpi.conversionRate.value}</h3>
            <span className="ml-2 text-sm text-green-600">{campaignData.kpi.conversionRate.change} vs last period</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">vs. baseline period</p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600 mb-1">Average Basket</p>
          <div className="flex items-baseline">
            <h3 className="text-2xl font-semibold text-gray-900">{campaignData.kpi.averageBasket.value}</h3>
            <span className="ml-2 text-sm text-green-600">{campaignData.kpi.averageBasket.change} vs last period</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">vs. baseline period</p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600 mb-1">Incremental Volume</p>
          <div className="flex items-baseline">
            <h3 className="text-2xl font-semibold text-gray-900">{campaignData.kpi.incrementalVolume.value}</h3>
          </div>
          <p className="text-xs text-gray-500 mt-1">{campaignData.kpi.incrementalVolume.description}</p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600 mb-1">Campaign ROI</p>
          <div className="flex items-baseline">
            <h3 className="text-2xl font-semibold text-gray-900">{campaignData.kpi.roi.value}</h3>
            <span className="ml-2 text-sm text-green-600">{campaignData.kpi.roi.change} vs last period</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">vs. previous campaign</p>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('impact')}
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'impact' ? 'border-b-2 border-purple-600 text-purple-700' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Campaign Impact
            </button>
            <button
              onClick={() => setActiveTab('segment')}
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'segment' ? 'border-b-2 border-purple-600 text-purple-700' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Segment Performance
            </button>
            <button
              onClick={() => setActiveTab('customer')}
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'customer' ? 'border-b-2 border-purple-600 text-purple-700' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Customer Analysis
            </button>
          </div>
        </div>
        
        {activeTab === 'impact' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Before/After Impact Analysis (Margin in ฿/Liter)</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={campaignData.beforeAfterData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0.7, 0.9]} />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="before" 
                    name="Projected" 
                    stroke="#9CA3AF" 
                    strokeWidth={2} 
                    dot={{ r: 4 }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="after" 
                    name="Actual" 
                    stroke="#8B5CF6" 
                    strokeWidth={2} 
                    dot={{ r: 4 }} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Campaign Highlights</h4>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                      <TrendingUp size={16} className="text-green-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">Margin Improvement</h5>
                      <p className="text-sm text-gray-600">Consistent growth in margin throughout campaign period, exceeding projections by 0.06 ฿/L in August.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                      <BarChart3 size={16} className="text-green-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">Volume Growth</h5>
                      <p className="text-sm text-gray-600">87K incremental liters attributed to campaign, with strongest performance in Highway segment.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                      <LineChartIcon size={16} className="text-green-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">ROI Performance</h5>
                      <p className="text-sm text-gray-600">215% ROI exceeds target of 180%, driven by higher than expected premium fuel adoption.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Optimization Recommendations</h4>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                      <Clock size={16} className="text-purple-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">Campaign Extension</h5>
                      <p className="text-sm text-gray-600">Consider 2-week extension based on upward trajectory in August performance metrics.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                      <Users size={16} className="text-purple-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">Segment Expansion</h5>
                      <p className="text-sm text-gray-600">Expand to Suburban segment with modified offering to increase total reach by estimated 15%.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                      <Tag size={16} className="text-purple-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">Pricing Adjustment</h5>
                      <p className="text-sm text-gray-600">Refine discount tiers based on purchase volume data to optimize margin impact.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'segment' && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Segment Response</h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: 'Urban', value: 12.5 },
                        { name: 'Highway', value: 8.2 },
                        { name: 'Suburban', value: 5.7 },
                        { name: 'Rural', value: 2.3 },
                      ]}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 'dataMax + 2']} />
                      <YAxis dataKey="name" type="category" width={80} />
                      <Tooltip />
                      <Bar dataKey="value" name="Growth %" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Performance by Station Type</h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: 'Flagship', value: 15.3 },
                        { name: 'Standard', value: 10.1 },
                        { name: 'Express', value: 7.8 },
                        { name: 'Mini', value: 4.2 },
                      ]}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 'dataMax + 2']} />
                      <YAxis dataKey="name" type="category" width={80} />
                      <Tooltip />
                      <Bar dataKey="value" name="Growth %" fill="#6366F1" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Key Segment Insights</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border border-purple-100 rounded-lg bg-purple-50">
                  <h4 className="font-medium text-purple-900 mb-2">Urban Segment Performance</h4>
                  <ul className="text-sm space-y-2">
                    <li className="flex items-start">
                      <div className="w-4 h-4 rounded-full bg-green-500 mt-0.5 mr-2 flex-shrink-0"></div>
                      <span>Highest conversion rate at 24.5%, showing strong price elasticity</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-4 h-4 rounded-full bg-green-500 mt-0.5 mr-2 flex-shrink-0"></div>
                      <span>Premium fuel adoption increased by 18.7% during campaign</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-4 h-4 rounded-full bg-green-500 mt-0.5 mr-2 flex-shrink-0"></div>
                      <span>Cross-selling with Café Amazon achieved 32% attachment rate</span>
                    </li>
                  </ul>
                </div>
                
                <div className="p-4 border border-blue-100 rounded-lg bg-blue-50">
                  <h4 className="font-medium text-blue-900 mb-2">Highway Segment Performance</h4>
                  <ul className="text-sm space-y-2">
                    <li className="flex items-start">
                      <div className="w-4 h-4 rounded-full bg-green-500 mt-0.5 mr-2 flex-shrink-0"></div>
                      <span>Volume increase of 8.2% with significant weekend boost</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-4 h-4 rounded-full bg-yellow-500 mt-0.5 mr-2 flex-shrink-0"></div>
                      <span>Average transaction value up 6.3%, below Urban segment</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-4 h-4 rounded-full bg-green-500 mt-0.5 mr-2 flex-shrink-0"></div>
                      <span>Loyalty program enrollment increased by 22% during campaign</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'customer' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Response Analysis</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Customer Segments</h4>
                
                {campaignData.customerSegments.map((segment, index) => (
                  <div key={index} className="mb-3">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-700">{segment.name}</span>
                      <span className="text-sm font-medium text-gray-900">{segment.percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-green-500' : 'bg-purple-500'
                        }`}
                        style={{ width: `${segment.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Demographics</h4>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-semibold text-blue-600">{campaignData.demographics.male}%</div>
                    <div className="text-sm text-gray-600">Male</div>
                  </div>
                  <div>
                    <div className="text-3xl font-semibold text-purple-600">{campaignData.demographics.female}%</div>
                    <div className="text-sm text-gray-600">Female</div>
                  </div>
                  <div>
                    <div className="text-3xl font-semibold text-gray-600">{campaignData.demographics.other}%</div>
                    <div className="text-sm text-gray-600">Other</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">Customer Satisfaction</h4>
              
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-4xl font-semibold text-green-600">{campaignData.customerSatisfaction.value}</div>
                  <div className="text-sm text-gray-600 mt-1">Positive Feedback</div>
                  <div className="text-sm text-green-600">{campaignData.customerSatisfaction.change} during campaign</div>
                </div>
                
                <div className="w-32 h-32 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-xl font-semibold text-gray-900">92%</div>
                  </div>
                  <svg className="w-full h-full" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#E5E7EB"
                      strokeWidth="2"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#10B981"
                      strokeWidth="2"
                      strokeDasharray="92, 100"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
              
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-900 mb-1">Key Satisfaction Drivers</div>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Promotion transparency</li>
                    <li>• Easy redemption process</li>
                    <li>• Staff knowledge of promotion</li>
                  </ul>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-900 mb-1">Repeat Intent</div>
                  <div className="text-2xl font-semibold text-gray-900">78%</div>
                  <div className="text-xs text-green-600">+12% vs. baseline</div>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-900 mb-1">Recommendation Rate</div>
                  <div className="text-2xl font-semibold text-gray-900">81%</div>
                  <div className="text-xs text-green-600">+15% vs. baseline</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignDetail;
